import React, { useId, useState } from 'react';
import {
  Box,
  ClickAwayListener,
  Fade,
  Paper,
  Popper,
  Typography,
} from '@mui/material';
import type { PopperPlacementType } from '@mui/material/Popper';

/** Laranja PA (`fa-info-circle` legado) — Popper React do PV. */
export const PA_INFO_ICON_COLOR = '#FFA52A';

function InfoToolTipIcon({ clipId }: { clipId: string }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
      <g clipPath={`url(#${clipId})`}>
        <circle cx="8" cy="8" r="7" fill={PA_INFO_ICON_COLOR} />
        <path
          d="M8.5 7C8.77614 7 9 7.22386 9 7.5V11.5C9 11.7761 8.77614 12 8.5 12H7.5C7.22386 12 7 11.7761 7 11.5V7.5C7 7.22386 7.22386 7 7.5 7H8.5ZM8.5 4C8.77614 4 9 4.22386 9 4.5V5.5C9 5.77614 8.77614 6 8.5 6H7.5C7.22386 6 7 5.77614 7 5.5V4.5C7 4.22386 7.22386 4 7.5 4H8.5Z"
          fill="white"
        />
      </g>
      <defs>
        <clipPath id={clipId}>
          <rect width="16" height="16" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
}

function InfoToolTipXIcon({ clipId }: { clipId: string }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
      <g clipPath={`url(#${clipId})`}>
        <circle cx="8" cy="8" r="7" fill={PA_INFO_ICON_COLOR} />
        <path
          d="M5.52475 4.81802C5.72002 4.62276 6.0366 4.62276 6.23186 4.81802L7.99963 6.58579L9.76739 4.81802C9.96267 4.62294 10.2793 4.62282 10.4745 4.81802L11.1816 5.52513C11.3766 5.72034 11.3766 6.03702 11.1816 6.23223L9.41384 8L11.1816 9.76777C11.3769 9.96303 11.3769 10.2796 11.1816 10.4749L10.4745 11.182C10.2792 11.3772 9.96266 11.3772 9.76739 11.182L7.99963 9.41421L6.23186 11.182C6.03666 11.3772 5.72003 11.377 5.52475 11.182L4.81765 10.4749C4.62238 10.2796 4.62238 9.96303 4.81765 9.76777L6.58541 8L4.81765 6.23223C4.62238 6.03697 4.62238 5.72039 4.81765 5.52513L5.52475 4.81802Z"
          fill="white"
        />
      </g>
      <defs>
        <clipPath id={clipId}>
          <rect width="16" height="16" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
}

export interface FieldInfoPopperProps {
  text: string;
  placement?: PopperPlacementType;
  title?: string;
  /** Rótulo acessível do botão (i). */
  ariaLabel?: string;
}

/** Ícone (i) clicável com Popper — padrão PA/PV React (`layout/Popper`, cor PA `#FFA52A`). */
export function FieldInfoPopper({
  text,
  placement = 'right',
  title,
  ariaLabel = 'Mais informações',
}: FieldInfoPopperProps) {
  const clipId = useId().replace(/:/g, '');
  const clipIdX = `${clipId}x`;
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl((prev) => (prev ? null : event.currentTarget));
  };

  const handleClickAway = () => {
    setAnchorEl(null);
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
        <Popper
          sx={{ zIndex: 1400, border: '1px solid #c2c2c2', maxWidth: 300 }}
          open={open}
          anchorEl={anchorEl}
          placement={placement}
          transition
          modifiers={[{ name: 'flip', enabled: false }]}
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={350}>
              <Paper elevation={2}>
                {title ? (
                  <Typography
                    sx={{ p: '4px', fontSize: 12, color: '#2F8E57', fontStyle: 'italic' }}
                  >
                    {title}
                  </Typography>
                ) : null}
                <Typography sx={{ p: '4px', fontSize: 12 }}>{text}</Typography>
              </Paper>
            </Fade>
          )}
        </Popper>
        <button
          type="button"
          onClick={handleClick}
          aria-label={ariaLabel}
          aria-expanded={open}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            display: 'inline-flex',
            lineHeight: 0,
          }}
        >
          {open ? <InfoToolTipXIcon clipId={clipIdX} /> : <InfoToolTipIcon clipId={clipId} />}
        </button>
      </Box>
    </ClickAwayListener>
  );
}
